import { Button, Box, Flex, useToast } from '@chakra-ui/react';
import { useContext } from 'react';
import { SalesTiers } from './SalesTiers';
import { useTranslation } from 'react-i18next';
import { fadeInLeftAnimation } from '@texas/resources/animations/animations';
import { ServerError } from '@texas/types';
import { emptyNodeGroup } from '@texas/utils/helpers/nodeGroupHelpers';
import { request } from '@texas/utils/helpers/httpHelpers';
import { CustomerCurrency } from '@texas/api/endpoints/currenciesApi';
import { PurchaseInformation } from '@texas/api/endpoints/purchaseInformationApi';
import { SalesInformation } from '@texas/api/endpoints/salesInformationApi';
import {
  SalesCurrency,
  salesCurrenciesApi,
} from '@texas/api/endpoints/salesCurrenciesApi';
import { TierPrice } from '@texas/api/endpoints/tierPriceApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { VerifyButton } from '@texas/components/shared/verifyButton/VerifyButton';
import { useActiveSession } from '@texas/hooks/useSession';
import { AdditionalChargeForm } from './forms/AdditionalChargeForm';
import { SalesCurrencyForm } from './forms/SalesCurrencyForm';
import { SalesContext } from './Sales';
import {
  getBranchDecimalsInPrice,
  resolveBranchDecimalsInPrice,
} from './shared';

interface SalesCurrencyComponentProps {
  salesCurrency: SalesCurrency;
  currencies: CustomerCurrency[];
  selectedCurrency: CustomerCurrency;
  sellingExchangeRate: number;
  purchaseInformation: PurchaseInformation;
  salesInformation: SalesInformation;
  tierPrices: TierPrice[];
  onRemoved: () => void;
}

export function SalesCurrencyComponent(props: SalesCurrencyComponentProps) {
  const session = useActiveSession();
  const salesContext = useContext(SalesContext);
  const toast = useToast();
  const { t } = useTranslation();

  const removeSalesCurrencyRequest = useApiRequest(
    salesCurrenciesApi.deleteSalesCurrency,
  );
  const setMainRequest = useApiRequest(
    salesCurrenciesApi.setSalesCurrencyAsMain,
  );

  const isDefaultCurrency =
    session.branches.find((b) => b.id === props.salesInformation.branchId)
      ?.defaultCurrencyId === props.salesCurrency.currencyId;
  const branchId = props.salesInformation.branchId;
  const branchDecimalsInPrice = resolveBranchDecimalsInPrice(
    props.tierPrices,
    branchId,
  );
  const decimalsInPrice = getBranchDecimalsInPrice(
    props.tierPrices.filter((t) => t.branchId === branchId),
    branchDecimalsInPrice,
  );

  return (
    <Flex direction="column">
      {!props.salesCurrency.isMain && (
        <Flex
          p={4}
          gap={2}
          align="start"
          mr="auto"
          w="100%"
          borderBottom="1px solid"
          backgroundColor="texas.bg.blueTint.900"
          borderColor="gray.800"
          _light={{ backgroundColor: 'white', borderColor: 'gray.100' }}
        >
          <Button
            isLoading={setMainRequest.loading}
            isDisabled={setMainRequest.loading}
            size="sm"
            alignSelf="center"
            variant="texas-solid"
            disabled={props.salesInformation.isReady}
            onClick={() => {
              setMainCurrency(props.salesCurrency.id);
            }}
          >
            Set {props.salesCurrency.currencyCode} as main
          </Button>
          {!isDefaultCurrency && (
            <VerifyButton
              buttonProps={{
                size: 'sm',
                alignSelf: 'center',
                isLoading: removeSalesCurrencyRequest.loading,
                isDisabled:
                  removeSalesCurrencyRequest.loading ||
                  props.salesInformation.isReady,
              }}
              onVerified={() => {
                removeCurrencyRequest(props.salesCurrency.id);
              }}
              label="Remove currency"
            />
          )}
        </Flex>
      )}
      <Box padding={4} animation={fadeInLeftAnimation(200)} w="100%">
        <Flex direction="column">
          <Flex gap={2}>
            <SalesCurrencyForm
              isBranchDefaultCurrency={isDefaultCurrency}
              salesCurrency={props.salesCurrency}
              disabled={props.salesInformation.isReady}
              branchId={props.salesInformation.branchId}
              variantId={props.salesInformation.variantId}
            />
            <AdditionalChargeForm
              additionalCharge={props.salesCurrency.additionalCharge}
              salesCurrency={props.salesCurrency}
              sellingExchangeRate={props.sellingExchangeRate}
              purchaseInformation={props.purchaseInformation}
              isDisabled={props.salesInformation.isReady}
              selectedCurrency={props.selectedCurrency}
              decimalsInPrice={decimalsInPrice}
            />
          </Flex>
        </Flex>

        <SalesTiers
          salesCurrency={props.salesCurrency}
          nodeGroups={salesContext?.articleNodeGroup ?? emptyNodeGroup}
          purchaseInformation={props.purchaseInformation}
          tierPrices={props.tierPrices}
          selectedCurrency={props.selectedCurrency}
          sellingExchangeRate={props.sellingExchangeRate}
          readyForSync={props.salesInformation.isReady}
        />
      </Box>
    </Flex>
  );

  async function setMainCurrency(salesCurrencyId: number) {
    await request(
      setMainRequest.request,
      [salesCurrencyId],
      (data) => {
        toast({
          title: t('variant.mainCurrencySet'),
          status: 'success',
          isClosable: true,
        });
        const toSet = salesContext?.salesInformation.data?.map((s) => {
          if (s.id === props.salesInformation.id) {
            return { ...s, currencies: data };
          }

          return s;
        });

        if (!toSet) {
          return;
        }

        salesContext?.salesInformation.set(toSet);
      },
      (error: ServerError) => {
        toast({
          title: 'Failed to set main currency',
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  }

  async function removeCurrencyRequest(salesCurrencyId: number) {
    await request(
      removeSalesCurrencyRequest.request,
      [salesCurrencyId],
      () => {
        toast({
          title: 'Currency removed',
          status: 'success',
          isClosable: true,
        });
        const toSet = salesContext?.salesInformation.data?.map((s) => {
          if (s.id === props.salesInformation.id) {
            return {
              ...s,
              currencies: s.currencies.filter((s) => s.id !== salesCurrencyId),
            };
          }

          return s;
        });

        if (!toSet) {
          return;
        }

        salesContext?.salesInformation.set(toSet);
        props.onRemoved();
      },
      (error: ServerError) => {
        toast({
          title: 'Failed to remove currency',
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  }
}
